// Centralized Employee Mock Data - Global Healthcare Enterprise
import { 
  COMPANY_INFO, 
  GLOBAL_LOCATIONS, 
  DEPARTMENTS, 
  JOB_LEVELS, 
  CURRENCIES,
  formatCurrency,
  convertCurrency,
  getLocationById
} from '../constants/enterpriseData';

export interface Employee {
  id: string;
  name: string;
  email: string;
  position: string;
  department: string;
  businessUnit: string;
  team: string;
  level: string;
  grade: string;
  tenure: string;
  location: string;
  country: string;
  region: string;
  roleLevel: string;
  performance: number;
  performanceScore: string;
  engagement: number;
  engagementScore: string;
  attritionRisk: number;
  riskLevel: string;
  salary: number;
  currency: string;
  baseSalaryEUR: number;
  lastReview: string;
  manager: string;
  managerId?: string;
  directReports: number;
  skills: string[];
  status: string;
  hireDate: Date;
  age: number;
  gender: 'Male' | 'Female' | 'Non-binary' | 'Prefer not to say';
  diversity: boolean;
  citizenship: string;
  workModel: 'Remote' | 'Hybrid' | 'On-site';
  contractType: 'Full-time' | 'Part-time' | 'Contractor';
  education: string;
  certifications: string[];
  languages: string[];
  isManager: boolean;
  totalCompensation: number;
  bonus: number;
  equity: number;
  benefits: number;
  lastPromotion?: Date;
  potentialRating: 'High' | 'Medium' | 'Low';
  successorReadiness: 'Ready Now' | '1-2 Years' | '2+ Years' | 'Not Ready';
  criticalRole: boolean;
  flightRisk: boolean;
  keyTalent: boolean;
}

// Generate realistic employee data based on global healthcare enterprise
const firstNames = {
  male: ['James', 'Michael', 'Robert', 'John', 'David', 'William', 'Richard', 'Thomas', 'Christopher', 'Daniel', 'Matthew', 'Anthony', 'Mark', 'Donald', 'Steven', 'Paul', 'Andrew', 'Joshua', 'Kenneth', 'Kevin', 'Brian', 'George', 'Timothy', 'Ronald', 'Jason', 'Edward', 'Jeffrey', 'Ryan', 'Jacob', 'Gary', 'Nicholas', 'Eric', 'Jonathan', 'Stephen', 'Larry', 'Justin', 'Scott', 'Brandon', 'Benjamin', 'Samuel'],
  female: ['Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen', 'Nancy', 'Lisa', 'Betty', 'Helen', 'Sandra', 'Donna', 'Carol', 'Ruth', 'Sharon', 'Michelle', 'Laura', 'Sarah', 'Kimberly', 'Deborah', 'Dorothy', 'Lisa', 'Nancy', 'Karen', 'Betty', 'Helen', 'Sandra', 'Donna', 'Carol', 'Ruth', 'Sharon', 'Michelle', 'Laura', 'Amy', 'Kathleen', 'Angela'],
  global: ['Wei', 'Ahmed', 'Raj', 'Priya', 'Maria', 'Ana', 'Carlos', 'Luis', 'Zhang', 'Li', 'Kim', 'Park', 'Tanaka', 'Sato', 'Mueller', 'Schmidt', 'Hans', 'Klaus', 'Pierre', 'Jean', 'Giovanni', 'Marco', 'Abdul', 'Hassan', 'Fatima', 'Aisha', 'Chen', 'Wu', 'Huang', 'Singh', 'Kumar', 'Patel', 'Rodriguez', 'Garcia', 'Martinez', 'Lopez', 'Gonzalez', 'Hernandez', 'Silva', 'Santos']
};

const lastNames = {
  western: ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores'],
  european: ['Mueller', 'Schmidt', 'Schneider', 'Fischer', 'Weber', 'Meyer', 'Wagner', 'Becker', 'Schulz', 'Hoffmann', 'Rossi', 'Russo', 'Ferrari', 'Esposito', 'Bianchi', 'Romano', 'Colombo', 'Ricci', 'Marino', 'Greco', 'Bruno', 'Gallo', 'Conti', 'De Luca', 'Martin', 'Bernard', 'Dubois', 'Thomas', 'Robert', 'Petit', 'Durand', 'Leroy', 'Moreau', 'Simon', 'Laurent', 'Lefebvre', 'Michel', 'Garcia', 'David', 'Bertrand'],
  asian: ['Zhang', 'Wang', 'Li', 'Liu', 'Chen', 'Yang', 'Huang', 'Wu', 'Zhou', 'Xu', 'Sun', 'Ma', 'Zhu', 'Hu', 'Guo', 'He', 'Gao', 'Lin', 'Luo', 'Zheng', 'Kim', 'Lee', 'Park', 'Choi', 'Jung', 'Kang', 'Cho', 'Yoon', 'Jang', 'Lim', 'Tanaka', 'Sato', 'Suzuki', 'Takahashi', 'Watanabe', 'Ito', 'Yamamoto', 'Nakamura', 'Kobayashi', 'Saito']
};

const healthcareSkills = [
  'Clinical Research', 'Medical Device Development', 'Regulatory Affairs', 'FDA Compliance',
  'ISO 13485', 'Quality Management', 'Risk Management', 'Clinical Data Management',
  'Biostatistics', 'Epidemiology', 'Health Economics', 'Pharmacovigilance',
  'Data Science', 'Machine Learning', 'Artificial Intelligence', 'Python',
  'R Programming', 'SQL', 'Tableau', 'Power BI', 'Healthcare Analytics',
  'Cloud Computing', 'AWS', 'Azure', 'Software Development', 'Java',
  'C#', '.NET', 'JavaScript', 'React', 'Node.js', 'DevOps',
  'Project Management', 'Agile', 'Scrum', 'PMP', 'Six Sigma',
  'Lean Manufacturing', 'Process Improvement', 'Change Management',
  'Leadership', 'Team Management', 'Strategic Planning', 'Budget Management',
  'Clinical Operations', 'GCP', 'Clinical Trials', 'Protocol Development',
  'Biomedical Engineering', 'Medical Devices', 'Product Development',
  'Supply Chain Management', 'Procurement', 'Vendor Management',
  'Compliance Management', 'Audit Management', 'Training Development',
  'Digital Health', 'Telemedicine', 'Health Information Systems',
  'HIPAA', 'Patient Privacy', 'Data Security', 'Cybersecurity'
];

const educationLevels = [
  'High School', 'Associates Degree', 'Bachelor\'s Degree', 'Master\'s Degree', 
  'MBA', 'PhD', 'MD', 'Pharm.D', 'RN', 'Other Professional Degree'
];

const certifications = [
  'PMP', 'Six Sigma Black Belt', 'Six Sigma Green Belt', 'CRA', 'CCRP',
  'RAC', 'CQA', 'CQE', 'CPHQ', 'CHPS', 'RHIA', 'RHIT', 'CCS',
  'AWS Certified', 'Azure Certified', 'Google Cloud Certified',
  'Certified Data Scientist', 'Certified Analytics Professional',
  'Lean Six Sigma', 'ITIL', 'Agile Certified', 'Scrum Master'
];

function getRandomElement<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

function getRandomElements<T>(array: T[], count: number): T[] {
  const shuffled = array.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

function generateRandomName(location: any): { firstName: string; lastName: string; gender: string } {
  const genders = ['Male', 'Female'];
  const gender = getRandomElement(genders);
  
  let firstNamePool: string[];
  let lastNamePool: string[];
  
  // Select names based on region
  if (location.region === 'Asia-Pacific' && ['China', 'Japan', 'South Korea'].includes(location.country)) {
    firstNamePool = firstNames.global;
    lastNamePool = lastNames.asian;
  } else if (location.region === 'Europe') {
    firstNamePool = [...firstNames.male, ...firstNames.female, ...firstNames.global.slice(0, 10)];
    lastNamePool = [...lastNames.western, ...lastNames.european];
  } else {
    firstNamePool = gender === 'Male' ? firstNames.male : firstNames.female;
    lastNamePool = lastNames.western;
  }
  
  return {
    firstName: getRandomElement(firstNamePool),
    lastName: getRandomElement(lastNamePool),
    gender
  };
}

function calculateTotalCompensation(baseSalary: number, level: string, location: any): any {
  const jobLevel = JOB_LEVELS.find(l => l.id === level);
  const isManager = level.startsWith('M');
  const isExecutive = ['M6', 'M7', 'M8', 'M9'].includes(level);
  
  // Bonus calculation (percentage of base salary)
  let bonusPercentage = 0;
  if (isExecutive) bonusPercentage = 0.4 + Math.random() * 0.3; // 40-70%
  else if (isManager) bonusPercentage = 0.15 + Math.random() * 0.15; // 15-30%
  else if (level.includes('L4') || level.includes('L5') || level.includes('L6')) bonusPercentage = 0.1 + Math.random() * 0.1; // 10-20%
  else bonusPercentage = 0.05 + Math.random() * 0.05; // 5-10%
  
  const bonus = baseSalary * bonusPercentage;
  
  // Equity (for senior levels only)
  let equity = 0;
  if (isExecutive) equity = baseSalary * (0.2 + Math.random() * 0.3); // 20-50%
  else if (level.includes('M4') || level.includes('M5')) equity = baseSalary * (0.1 + Math.random() * 0.1); // 10-20%
  else if (level.includes('L5') || level.includes('L6')) equity = baseSalary * (0.05 + Math.random() * 0.1); // 5-15%
  
  // Benefits (fairly standard across locations)
  const benefits = baseSalary * (0.25 + Math.random() * 0.1); // 25-35%
  
  return {
    bonus: Math.round(bonus),
    equity: Math.round(equity),
    benefits: Math.round(benefits),
    totalCompensation: Math.round(baseSalary + bonus + equity + benefits)
  };
}

export function generateGlobalEmployees(count: number = 1000): Employee[] {
  const employees: Employee[] = [];
  
  for (let i = 0; i < count; i++) {
    // Select random location
    const location = getRandomElement(GLOBAL_LOCATIONS);
    const department = getRandomElement(DEPARTMENTS);
    const businessUnit = department.businessUnit;
    
    // Select appropriate job level based on department
    let jobLevel: any;
    if (department.id === 'clinical-operations' || department.id === 'nursing' || department.id === 'patient-services') {
      // Clinical roles tend to have different progression
      jobLevel = getRandomElement(JOB_LEVELS.filter(l => ['L1', 'L2', 'L3', 'M1', 'M2'].includes(l.id)));
    } else if (department.id === 'human-resources' || department.id === 'finance' || department.id === 'legal') {
      // Corporate functions
      jobLevel = getRandomElement(JOB_LEVELS.filter(l => ['L2', 'L3', 'L4', 'M1', 'M2', 'M3', 'M4'].includes(l.id)));
    } else if (department.id === 'medical-devices' || department.id === 'software-development' || department.id === 'data-analytics') {
      // Technical roles
      jobLevel = getRandomElement(JOB_LEVELS.filter(l => ['L2', 'L3', 'L4', 'L5', 'L6', 'M1', 'M2', 'M3'].includes(l.id)));
    } else {
      jobLevel = getRandomElement(JOB_LEVELS);
    }
    
    const { firstName, lastName, gender } = generateRandomName(location);
    const isManager = jobLevel.id.startsWith('M');
    
    // Calculate salary in local currency
    const baseSalaryEUR = jobLevel.minSalary + Math.random() * (jobLevel.maxSalary - jobLevel.minSalary);
    const localCurrency = location.currency;
    const localSalary = convertCurrency(baseSalaryEUR, 'EUR', localCurrency);
    
    // Calculate tenure
    const tenureMonths = Math.floor(Math.random() * 240); // 0-20 years
    const tenureYears = Math.floor(tenureMonths / 12);
    const hireDate = new Date();
    hireDate.setMonth(hireDate.getMonth() - tenureMonths);
    
    // Calculate age
    const age = 22 + Math.floor(Math.random() * 43); // 22-65
    
    // Performance and engagement scores
    const performance = Math.random() * 100;
    const engagement = Math.random() * 100;
    const attritionRisk = Math.random() * 100;
    
    // Determine diversity status
    const diversity = gender === 'Female' || Math.random() < 0.15; // Women + other diversity factors
    
    const compensationData = calculateTotalCompensation(baseSalaryEUR, jobLevel.id, location);
    
    const employee: Employee = {
      id: `emp_${i + 1}`,
      name: `${firstName} ${lastName}`,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@cloudonmars.com`,
      position: `${jobLevel.name} ${department.name}`,
      department: department.name,
      businessUnit: businessUnit,
      team: `Team ${String.fromCharCode(65 + Math.floor(Math.random() * 10))}`, // Team A-J
      level: jobLevel.id,
      grade: jobLevel.grade,
      tenure: tenureYears > 0 ? `${tenureYears} years` : `${tenureMonths} months`,
      location: location.city,
      country: location.country,
      region: location.region,
      roleLevel: jobLevel.name,
      performance: Math.round(performance),
      performanceScore: performance > 80 ? 'Exceptional' : performance > 65 ? 'Exceeds' : performance > 50 ? 'Meets' : performance > 35 ? 'Below' : 'Needs Improvement',
      engagement: Math.round(engagement),
      engagementScore: engagement > 75 ? 'Highly Engaged' : engagement > 50 ? 'Engaged' : engagement > 25 ? 'Neutral' : 'Disengaged',
      attritionRisk: Math.round(attritionRisk),
      riskLevel: attritionRisk > 75 ? 'High' : attritionRisk > 50 ? 'Medium' : 'Low',
      salary: Math.round(localSalary),
      currency: localCurrency,
      baseSalaryEUR: Math.round(baseSalaryEUR),
      lastReview: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      manager: isManager ? '' : `Manager ${Math.floor(Math.random() * 50) + 1}`,
      managerId: isManager ? undefined : `mgr_${Math.floor(Math.random() * 100) + 1}`,
      directReports: isManager ? Math.floor(Math.random() * 15) + 1 : 0,
      skills: getRandomElements(healthcareSkills, Math.floor(Math.random() * 8) + 3),
      status: Math.random() > 0.95 ? 'Inactive' : 'Active',
      hireDate,
      age,
      gender: gender as any,
      diversity,
      citizenship: location.country,
      workModel: getRandomElement(['Remote', 'Hybrid', 'On-site']),
      contractType: Math.random() > 0.85 ? getRandomElement(['Part-time', 'Contractor']) : 'Full-time',
      education: getRandomElement(educationLevels),
      certifications: getRandomElements(certifications, Math.floor(Math.random() * 3)),
      languages: location.country === 'Germany' ? ['German', 'English'] : 
                 location.country === 'China' ? ['Chinese', 'English'] :
                 location.country === 'Japan' ? ['Japanese', 'English'] :
                 location.country === 'Brazil' ? ['Portuguese', 'English'] :
                 location.country === 'Spain' ? ['Spanish', 'English'] :
                 ['English'],
      isManager,
      totalCompensation: compensationData.totalCompensation,
      bonus: compensationData.bonus,
      equity: compensationData.equity,
      benefits: compensationData.benefits,
      lastPromotion: tenureYears > 2 ? new Date(Date.now() - Math.random() * 730 * 24 * 60 * 60 * 1000) : undefined,
      potentialRating: getRandomElement(['High', 'Medium', 'Low']),
      successorReadiness: isManager ? getRandomElement(['Ready Now', '1-2 Years', '2+ Years', 'Not Ready']) : 'Not Ready',
      criticalRole: Math.random() > 0.85,
      flightRisk: attritionRisk > 75,
      keyTalent: performance > 85 && Math.random() > 0.7
    };
    
    employees.push(employee);
  }
  
  return employees;
}

// Pre-generated global employee dataset
// Generate a larger, more realistic dataset for enterprise scale
// Using lazy loading and virtualization for performance
export const globalEmployees = generateGlobalEmployees(10000); // Generate 10K for better representation

// Function to get paginated employees
export function getPaginatedEmployees(page: number, pageSize: number): Employee[] {
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return globalEmployees.slice(start, end);
}


// Filter employees by role for different dashboards
export function filterEmployeesByRole(employees: Employee[], role: string): Employee[] {
  switch (role) {
    case 'HR_LEADER':
      return employees; // Full access
    case 'MANAGER':
      // Managers see their reports and aggregated data
      return employees.filter(emp => 
        emp.isManager || Math.random() > 0.7 // Simulated team members
      );
    case 'RECRUITER':
      // Recruiters see candidate pipeline data
      return employees.filter(emp => 
        emp.status === 'Active' && Math.random() > 0.8 // Subset for recruitment context
      );
    default:
      return employees.slice(0, 100); // Limited view for other roles
  }
}

// Get employees by location
export function getEmployeesByLocation(locationId: string): Employee[] {
  const location = getLocationById(locationId);
  if (!location) return [];
  
  return globalEmployees.filter(emp => emp.location === location.city);
}


// Get employees by business unit
export function getEmployeesByBusinessUnit(businessUnitId: string): Employee[] {
  return globalEmployees.filter(emp => emp.businessUnit === businessUnitId);
}

// Calculate global diversity metrics
export function calculateDiversityMetrics(employees: Employee[] = globalEmployees) {
  const total = employees.length;
  const femaleCount = employees.filter(emp => emp.gender === 'Female').length;
  const diverseCount = employees.filter(emp => emp.diversity).length;
  const managementCount = employees.filter(emp => emp.isManager).length;
  const femaleInManagement = employees.filter(emp => emp.isManager && emp.gender === 'Female').length;
  
  return {
    totalEmployees: total,
    femalePercentage: (femaleCount / total) * 100,
    diversityPercentage: (diverseCount / total) * 100,
    femaleInManagementPercentage: managementCount > 0 ? (femaleInManagement / managementCount) * 100 : 0,
    avgAge: employees.reduce((sum, emp) => sum + emp.age, 0) / total,
    avgTenure: employees.reduce((sum, emp) => {
      const tenureMatch = emp.tenure.match(/(\d+)/);
      return sum + (tenureMatch ? parseInt(tenureMatch[1]) : 0);
    }, 0) / total
  };
}

// Get salary benchmarks by level and location
export function getSalaryBenchmarks(level: string, locationId?: string) {
  let relevantEmployees = globalEmployees.filter(emp => emp.level === level);
  
  if (locationId) {
    const location = getLocationById(locationId);
    if (location) {
      relevantEmployees = relevantEmployees.filter(emp => emp.location === location.city);
    }
  }
  
  if (relevantEmployees.length === 0) return null;
  
  const salaries = relevantEmployees.map(emp => emp.baseSalaryEUR).sort((a, b) => a - b);
  const totalComp = relevantEmployees.map(emp => emp.totalCompensation).sort((a, b) => a - b);
  
  return {
    count: relevantEmployees.length,
    salary: {
      min: Math.min(...salaries),
      max: Math.max(...salaries),
      median: salaries[Math.floor(salaries.length / 2)],
      p25: salaries[Math.floor(salaries.length * 0.25)],
      p75: salaries[Math.floor(salaries.length * 0.75)]
    },
    totalCompensation: {
      min: Math.min(...totalComp),
      max: Math.max(...totalComp),
      median: totalComp[Math.floor(totalComp.length / 2)],
      p25: totalComp[Math.floor(totalComp.length * 0.25)],
      p75: totalComp[Math.floor(totalComp.length * 0.75)]
    }
  };
}